import $ from 'jquery';

const FB_CONNECT_BUTTON_SELECTOR = '[data-fb-connect-button]';
const fbConfig = mpro.config.facebook;
// const langLocale = mpro.getCurrentLocale();
let fbIsReady = false;
let fbIsInitializing = false;

/**
 * Custom Xhr Error type
 */
export class ToastError extends Error {

	static NAME = 'ToastError';

	/**
	 * @param {string} type
	 * @param {string} title
	 */
	constructor(type, title, message) {
		super(message);
		this.type = type;
		this.title = title;
		// @ts-ignore
		// eslint-disable-next-line unicorn/custom-error-definition
		this.name = this.constructor.NAME;
	}

	get content() {
		return this.message;
	}

	toString() {
		/** @type {any} */
		const ctr = this.constructor;
		return `${ctr.NAME} (type: ${this.type}) : ${this.message}`;
	}
}

/**
 * Hook to provide the FB api only when it's ready
 *
 */
export const fbInitNotify = () => {

	// Ready
	if (fbIsReady === true) {
		return Promise.resolve(window.FB);
	}
	// Init promise is running
	else if (fbIsInitializing === true) {
		return new Promise((resolve, reject) => {
			mpro.once('fb-ready', () => {
				resolve(window.FB);
			});
		});
	}
	// First time requesting FB init

	return fbInitializeLib();

};

/**
 * @returns {Promise<any>}
 */
export const fbInitializeLib = () => {

	// FB api not present
	if (!window.FB && !fbIsInitializing) {

		// Assume init is done in the page template
		let doInit = false;

		fbIsInitializing = true;

		// No FB api script present
		if ($('script[src*="connect.facebook.net"]').length === 0) {

			// Init after FB api is ready
			doInit = true;

			// Add the fb api script to dom
			// $(`<script async defer crossorigin="anonymous" src="https://connect.facebook.net/${langLocale || 'en_US'}/sdk.js"></script>`).appendTo(document.body);
			// Problems styling translated content (texts too long) - always include en version
			$('<script async defer crossorigin="anonymous" src="https://connect.facebook.net/en_US/sdk.js"></script>').appendTo(document.body);
		}

		// Save previous fb init function, in case there was any
		const lastFbInit = window.fbAsyncInit;

		return new Promise((resolve, reject) => {
			// Add new fb init function
			window.fbAsyncInit = function() {

				const { FB } = window;

				if (doInit === true) {
					FB.init({
						appId: fbConfig.app_id,
						autoLogAppEvents: true,
						// Check login status
						status: false,
						xfbml: true,
						// oauth: true,
						version: fbConfig.sdk_version,
					});
				}

				fbIsReady = true;
				fbIsInitializing = false;
				mpro.emit('fb-ready', FB);

				// Call the previous init function, if any
				try {
					// Run the saved init function
					lastFbInit && lastFbInit();
					// Run the after FB init hooks
					afterFbInit();
				}
				catch (error) {
					console.warn('Error running page window.fbAsyncInit callback: ', error);
				}

				// Ready
				resolve(FB);
			};
		});
	}
	// Let notify handle the resolve
	else if (fbIsInitializing === true) {
		return fbInitNotify();
	}
	// FB is ready


	const { FB } = window;
	// Lib is ready
	fbIsReady = true;

	// Ready
	return Promise.resolve(FB);

};

const afterFbInit = () => {
	if (fbConfig.requires_unlink) {
		fbClearPerms();
	}
};

/**
 * Do login with Facebook
 *
 * @param {string} urlConnect
 * @param {string} scope
 */
export const fbLogin = async (urlConnect, scope) => {

	// Wait for lib to be ready
	const FB = await fbInitNotify();

	if (!urlConnect) {
		urlConnect = fbConfig.connect_url;
	}

	if (!scope) {
		scope = fbConfig.connect_permissions;
	}

	// Login to Facebook api
	FB.login((response) => {
		if (response.status === 'connected') {
			// Check for required permissions then do to the connect page
			fbCheckPerms(response)
				.then(_ => void fbRedirectConnect(urlConnect, response))
				.catch((error) => {

					let alertData;

					if (error instanceof Error) {
						console.warn('FB connect error', error);
						alertData = !(error instanceof ToastError) ? { format: 'error', title: 'Error', content: mpro.langvar('error_unknown') } : { type: error.type, title: error.title, content: error.content };
					}
					else {
						alertData = error;
					}

					void mpro.toast(alertData);
				});
		}
		// User cancelled
		else {
			// Do nothing
		}
	}, {
		scope,
		return_scopes: true,
	});
};

/**
 * Redirect to the connect backed
 *
 * @param {string} urlConnect
 * @param {*} response
 */
const fbRedirectConnect = (urlConnect, response) => {

	const auth = response.authResponse || {};
	const redirectTo = urlConnect +
		((urlConnect.search(/\?/igu) !== -1) ? '&' : '?') +
		'access_token=' + (auth.accessToken || '') +
		'&signed_request=' + (auth.signedRequest || '') +
		'&user_id=' + (auth.userID || '') +
		'&expires_in=' + (auth.expiresIn || '') +
		'&reauthorize_required_in=' + (auth.reauthorize_required_in || '');

	// Loading animation on the whole body
	mpro.addContentLoader(mpro.get$B());

	window.setTimeout(() => {
		window.location.href = redirectTo;
	}, 100);
};

const fbCheckPerms = (response) => {

	const { FB } = window;

	return new Promise((resolve, reject) => {
		// Connected
		if (response.status === 'connected') {
			// Access token used for api requests
			const { accessToken } = response.authResponse;

			FB.api('/me/permissions', 'get', { access_token: accessToken }, (response) => {
				if (Array.isArray(response.data) && (response.data.length > 0)) {

					const declinedScopes = [];

					// Search for declined permissions
					for (const v of response.data) {
						if (v.status === 'declined') {
							declinedScopes.push(v.permission);
						}
					}

					if (declinedScopes.length > 0) {
						reject(new ToastError('error', 'Permissions required', 'The following permissions are required in order to login with your Facebook account on this website: ' + declinedScopes.join(', ') + '<br>Please make sure you enabled all of the requested permissions and try again.'));
					}
					else {
						resolve();
					}
				}
				else {
					reject(new ToastError('error', 'Permissions required', 'Could not read any valid Facebook permission data. Please make sure you enabled all of the requested permissions and try again. <br>Also, please disable any ad-blockers on this website that may be interfering with the Facebook authentication process.'));
				}
			});
		}
		// Cancelled
		else {
			reject(new ToastError('error', 'Not connected', 'You are not logged into your Facebook account. <br>Please log into your Facebook account and try again.'));
		}
	});
};

const fbClearPerms = () => {
	const { FB } = window;
	try {
		FB.getLoginStatus((response) => {
			FB.api('/me/permissions', 'delete', (response) => {
				FB.logout((response) => {});
			});
		});
	}
	catch {
		console.warn('FB unlink error');
	}
};

// Add init function that is executed only on first dom-ready (it should contain only document/body delegate handlers)
mpro.addInitDelegate(($) => {

	const $B = mpro.get$B();

	// Facebook login link
	$B.on('click.facebookConnect', FB_CONNECT_BUTTON_SELECTOR, function(e) {
		e.preventDefault();
		const $button = $(this);
		const data = $button.data();
		const urlConnect = data.fbConnectUrl || fbConfig.connect_url;
		const scope = data.fbPermissions || fbConfig.connect_permissions;

		// Do login
		fbLogin(urlConnect, scope);
	});
});

export default mpro;
